/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from '@multilocale/react/index.jsx'
import IconPapillon from '@stiloso/icons/IconPapillon.jsx'
import languages from '@monorepool/shared/languages.js'
import LanguageDialog from '../dialogs/LanguageDialog.jsx'

const navigation = css({
  label: 'navigation',
  color: 'white',
  backgroundColor: '#4393d0',
  display: 'flex',
  position: 'fixed',
  justifyContent: 'space-between',
  top: 0,
  zIndex: 1000,
  width: '100%',
  height: 64,
})

const links = expanded =>
  css({
    label: 'links',
    overflow: 'hidden',
    height: expanded ? '100%' : '0px',
    width: '100%',
    visibility: expanded ? 'expanded' : 'collapse',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#4393d0',
    position: 'fixed',
    top: 64,
    left: 0,
    zIndex: 1000,
    transition: 'height 0.4s ease-in-out',
  })

const link = css({
  label: 'link',
  color: 'white',
  fontSize: 16,
  position: 'relative',
  height: 15,
  cursor: 'pointer',
  borderBottom: 'solid',
  borderBottomWidth: 1,
  borderBottomColor: '#DCDCDC',
  padding: 15,
  margin: '0px 20px',
  paddingLeft: 0,
})

const toggleMenuPage = css({
  label: 'drawer-container',
  cursor: 'pointer',
  height: 64,
  padding: '0px 20px',
  position: 'relative',
  width: 64,
})

const drawerIcon = expanded =>
  css({
    label: 'drawer-icon',
    width: 24,
    position: 'absolute',
    top: '40%',

    ':after, :before': {
      content: "''",
      width: 24,
      height: 2,
      position: 'absolute',
      backgroundColor: 'white',
      transition: '.3s ease-in-out',
      top: expanded ? 5 : 0,
      transform: expanded ? 'rotate(45deg)' : 0,
    },

    ':before': {
      top: expanded ? 5 : 10,
      transform: expanded ? 'rotate(-45deg)' : 0,
    },
  })

const NavigationSmartphoneComponent = ({ locale = 'en' }) => {
  const { t } = useTranslation(locale)
  const currentLanguage = languages[locale]
  const [expanded, setExpanded] = useState()
  const [languageDialog, setLanguageDialog] = useState()

  const toggleDrawer = () => {
    setExpanded(!expanded)
  }

  const toggleLanguageDialog = () => {
    setLanguageDialog(!languageDialog)
  }

  return (
    <div css={navigation}>
      <div css={toggleMenuPage} onClick={toggleDrawer}>
        <div css={drawerIcon(expanded)} data-testid="icon-drawer" />
      </div>

      <div css={links(expanded)}>
        <a css={link} href={`/${locale}/`}>
          {t('Home')}
        </a>
        <a css={link} href={`/blog/${locale}/`}>
          {t('Blog')}
        </a>
        <Link css={link} to={`/docs/${locale}/`}>
          {t('Documentation')}
        </Link>
        <a css={link} href={`/${locale}/pricing/`}>
          {t('Pricing')}
        </a>
        <a css={link} href={`/${locale}/downloads/`}>
          {t('Downloads')}
        </a>
        <a css={link} href="https://app.waiterio.com/signup">
          {t('Sign up')}
        </a>
        <a css={link} href="https://app.waiterio.com/login">
          {t('Login')}
        </a>
        <span onClick={toggleLanguageDialog} css={link}>
          {currentLanguage}
        </span>
      </div>

      <a href={`/${locale}/`} css={{ padding: 10 }}>
        <IconPapillon width="44" height="44" css={{ fill: 'white' }} />
      </a>

      <div css={{ width: 100 }} />

      {languageDialog && (
        <LanguageDialog currentLocale={locale} close={toggleLanguageDialog} />
      )}
    </div>
  )
}

export default NavigationSmartphoneComponent
